"use client";

import { CircleAlert } from "lucide-react";
import { useTranslations } from "next-intl";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface CoreProps {
  children: ReactNode;
  title: string;
  hint: string;
  retryLabel: string;
}

interface CoreState {
  failed: boolean;
}

class Core extends Component<CoreProps, CoreState> {
  state: CoreState = { failed: false };

  static getDerivedStateFromError(): CoreState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Once per failure: the host's console is the only place a broken payload
    // can be diagnosed, and the visitor sees no stack.
    console.error("[wexio] a tab crashed", error, info.componentStack);
  }

  // The failed subtree was unmounted when the error was caught, so showing the
  // children again mounts the tab from scratch.
  retry = () => this.setState({ failed: false });

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <CircleAlert aria-hidden="true" className="size-6 text-wx-fg-subtle" />
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-sm text-wx-fg">{this.props.title}</p>
          <p className="text-xs text-wx-fg-muted">{this.props.hint}</p>
        </div>
        <Button type="button" variant="tonal" size="sm" onClick={this.retry}>
          {this.props.retryLabel}
        </Button>
      </div>
    );
  }
}

/**
 * Keeps a crash inside one tab. Without it, a malformed payload (`undefined.map`
 * in a help list, a missing `meta`) unmounted the whole widget, launcher and tab
 * bar included. The shell wraps each route's content in this; a failure shows one
 * calm state with a Retry that mounts the tab again, and the rest of the widget
 * keeps working.
 */
export function TabErrorBoundary({ children }: { children: ReactNode }) {
  const t = useTranslations("tabError");
  return (
    <Core title={t("title")} hint={t("hint")} retryLabel={t("retry")}>
      {children}
    </Core>
  );
}
